import React, { useState, useEffect } from 'react';
import VehicleSearch from './VehicleSearch';
import TemplateDownload from './TemplateDownload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hook to detect mobile screen
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

function MRReport({ token }) {
  const isMobile = useIsMobile();
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [viewData, setViewData] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({});

  useEffect(() => {
    fetchVehicles();
    fetchUploadStatus();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setVehicles(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchUploadStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mr/upload-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      // Convert to lookup object
      const statusMap = {};
      data.vehicles.forEach(v => {
        statusMap[v.vehicle_no] = v;
      });
      setUploadStatus(statusMap);
    } catch (error) {
      console.error('Error fetching upload status:', error);
    }
  };

  const getSelectedVehicleStatus = () => {
    if (!selectedVehicle || !uploadStatus[selectedVehicle]) return null;
    return uploadStatus[selectedVehicle];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-SG', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !selectedVehicle) return;

    setLoading(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('vehicle_no', selectedVehicle);
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/mr/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const data = await response.json();
      
      // Show appropriate message based on merge or new
      if (data.new_records_added !== undefined) {
        setMessage(`✅ ${data.message}. ${data.new_records_added} new records added. Total: ${data.total_records} records.`);
      } else {
        setMessage(`✅ ${data.message}. ${data.records_count} records uploaded.`);
      }
      
      setFile(null);
      
      // Refresh upload status
      fetchUploadStatus();
      
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
      console.error('Upload error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewData = async () => {
    if (!selectedVehicle) return;

    setLoading(true);
    setViewData(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/mr/${selectedVehicle}/data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load data');
      }

      const data = await response.json();
      setViewData(data);
      setMessage(`✅ Loaded ${data.records?.length || 0} records`);
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
      console.error('View data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!selectedVehicle) return;

    setLoading(true);
    setMessage('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/mr/${selectedVehicle}/generate-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate PDF');
      }

      const data = await response.json();

      // Download PDF
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${data.pdf}`;
      link.download = data.filename;
      link.click();

      setMessage('✅ PDF generated and downloaded!');
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
      console.error('PDF generation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateExcel = async () => {
    if (!selectedVehicle) return;

    setLoading(true);
    setMessage('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/mr/${selectedVehicle}/generate-excel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate Excel');
      }

      const data = await response.json();

      // Download Excel
      const link = document.createElement('a');
      link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data.excel}`;
      link.download = data.filename;
      link.click();

      setMessage('✅ Excel generated and downloaded!');
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
      console.error('Excel generation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReport = async () => {
    if (!selectedVehicle) return;

    setLoading(true);
    setMessage('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/mr/${selectedVehicle}/save-report`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save report');
      }

      const data = await response.json();
      setMessage(`✅ ${data.message} - ${data.report_name}. You can now attach this report to repair requests.`);
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
      console.error('Save report error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="mr-report-page">
      <div className="page-header">
        <h2 style={isMobile ? { fontSize: '1.25rem' } : {}}>M&R Report Generator</h2>
        <p style={isMobile ? { fontSize: '0.8rem' } : {}}>Upload Excel M&R data and generate formatted PDF reports (7 years)</p>
      </div>

      {message && <div className="alert alert-success" data-testid="mr-message">{message}</div>}

      <div className="card" style={isMobile ? { padding: '1rem' } : {}}>
        <h3 className="card-title" style={isMobile ? { fontSize: '1rem' } : {}}>Upload M&R Data (Excel)</h3>
        
        {/* Last Upload Status Display */}
        {selectedVehicle && getSelectedVehicleStatus() && (
          <div style={{
            background: getSelectedVehicleStatus().has_mr_data ? '#F0FDF4' : '#FEF3C7',
            border: `1px solid ${getSelectedVehicleStatus().has_mr_data ? '#BBF7D0' : '#FDE68A'}`,
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '1.5rem' }}>
              {getSelectedVehicleStatus().has_mr_data ? '📊' : '⚠️'}
            </span>
            <div>
              <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '0.9rem' }}>
                {selectedVehicle} - M&R Data Status
              </div>
              <div style={{ color: '#6B7280', fontSize: '0.85rem' }}>
                {getSelectedVehicleStatus().has_mr_data ? (
                  <>
                    Last Upload: <strong>{formatDate(getSelectedVehicleStatus().last_upload_date)}</strong>
                    {' | '}
                    Total Records: <strong>{getSelectedVehicleStatus().total_records}</strong>
                    {' | '}
                    Upload Count: <strong>{getSelectedVehicleStatus().upload_count}</strong>
                  </>
                ) : (
                  'No M&R data uploaded yet. Upload now to enable historical analysis.'
                )}
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleUpload} data-testid="mr-upload-form">
          <div className="form-group">
            <label className="form-label">Select Vehicle (Type to search)</label>
            <VehicleSearch
              token={token}
              value={selectedVehicle}
              onChange={(value) => setSelectedVehicle(value)}
              placeholder="Type vehicle number..."
              required={true}
              dataTestId="mr-vehicle-search"
            />
            {!isMobile && (
              <p style={{fontSize: '0.875rem', color: '#64748B', marginTop: '0.5rem'}}>
                Start typing to see matching vehicles (e.g., XE, XD, YM)
              </p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Upload M&R File</label>
            
            {/* Template Download Section */}
            <div style={{ 
              background: '#F8FAFC', 
              padding: '0.75rem 1rem', 
              borderRadius: '6px', 
              marginBottom: '0.75rem',
              border: '1px solid #E2E8F0'
            }}>
              <TemplateDownload templateType="mr-report" token={token} compact={true} label="Download Sample Template" />
              <p style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '0.5rem', marginBottom: 0 }}>
                See the expected format with sample data rows.
              </p>
            </div>
            
            <input
              type="file"
              data-testid="mr-file-input"
              accept=".xlsx,.xls,application/pdf,.csv"
              onChange={(e) => setFile(e.target.files[0])}
              required
              style={isMobile ? { fontSize: '0.85rem' } : {}}
            />
            <p style={{ fontSize: '0.85rem', color: '#64748B', marginTop: '0.5rem' }}>
              Supports Excel (.xls, .xlsx), CSV, and PDF formats. 
              <strong style={{ color: '#059669' }}> New uploads merge with existing data</strong> - duplicates are automatically skipped.
            </p>
          </div>

          <button type="submit" className="btn btn-primary" data-testid="upload-mr-btn" disabled={loading} style={isMobile ? { width: '100%' } : {}}>
            {loading ? 'Uploading...' : 'Upload M&R Data'}
          </button>
        </form>
      </div>

      <div className="card" style={isMobile ? { padding: '1rem' } : {}}>
        <h3 className="card-title" style={isMobile ? { fontSize: '1rem' } : {}}>View/Generate M&R Report</h3>
        <p style={{ marginBottom: '1rem', color: '#64748B', fontSize: isMobile ? '0.8rem' : undefined }}>View uploaded data or generate formatted PDF/Excel report</p>
        <div className="form-group">
          <label className="form-label">Select Vehicle (Type to search)</label>
          <VehicleSearch
            token={token}
            value={selectedVehicle}
            onChange={(value) => {
              setSelectedVehicle(value);
              setViewData(null);
            }}
            placeholder="Type vehicle number..."
            dataTestId="pdf-vehicle-search"
          />
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            className="btn btn-secondary"
            data-testid="view-data-btn"
            onClick={handleViewData}
            disabled={!selectedVehicle || loading}
          >
            {loading ? 'Loading...' : '👁️ View Data'}
          </button>
          <button
            className="btn btn-primary"
            data-testid="generate-pdf-btn"
            onClick={handleGeneratePDF}
            disabled={!selectedVehicle || loading}
          >
            {loading ? 'Generating...' : '📄 Generate PDF Report'}
          </button>
          <button
            className="btn btn-success"
            data-testid="generate-excel-btn"
            onClick={handleGenerateExcel}
            disabled={!selectedVehicle || loading}
          >
            {loading ? 'Generating...' : '📊 Generate Excel Report'}
          </button>
          <button
            className="btn btn-secondary"
            data-testid="save-report-btn"
            onClick={handleSaveReport}
            disabled={!selectedVehicle || loading}
            style={{ background: '#8B5CF6', borderColor: '#8B5CF6', color: 'white' }}
          >
            {loading ? 'Saving...' : '💾 Save Report for Repairs'}
          </button>
        </div>
        <p style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '0.75rem' }}>
          💡 Tip: "Save Report for Repairs" stores this report so it can be attached to repair requests later.
        </p>
      </div>

      {/* M&R Search Section */}
      <MRSearchSection token={token} isMobile={isMobile} />

      {viewData && (
        <div className="card" data-testid="data-view">
          <h3 className="card-title">Uploaded M&R Data ({viewData.records?.length || 0} records)</h3>
          <p style={{ fontSize: '0.875rem', color: '#64748B', marginBottom: '1rem' }}>
            Uploaded: {new Date(viewData.uploaded_at).toLocaleString()} by {viewData.uploaded_by}
          </p>
          <div style={{ maxHeight: '600px', overflow: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  {viewData.records[0] && Object.keys(viewData.records[0]).map((key, idx) => (
                    <th key={idx}>{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {viewData.records.slice(0, 50).map((record, idx) => (
                  <tr key={idx}>
                    {Object.values(record).map((val, vidx) => (
                      <td key={vidx}>{val}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {viewData.records.length > 50 && (
              <p style={{ textAlign: 'center', padding: '1rem', color: '#64748B' }}>
                Showing first 50 of {viewData.records.length} records
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// M&R Search Section Component
function MRSearchSection({ token, isMobile }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchVehicle, setSearchVehicle] = useState('');
  const [searchVendor, setSearchVendor] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [costMin, setCostMin] = useState('');
  const [costMax, setCostMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery && !searchVehicle && !searchVendor) {
      setMessage('Please enter a search term');
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const params = new URLSearchParams();
      if (searchVehicle) params.append('vehicle_no', searchVehicle);
      if (searchVendor) params.append('vendor', searchVendor);
      if (searchQuery) params.append('description', searchQuery);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (costMin) params.append('cost_min', costMin);
      if (costMax) params.append('cost_max', costMax);
      params.append('limit', '100');

      const response = await fetch(`${BACKEND_URL}/api/mr/search?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setSearchResults(data);
      setMessage(`Found ${data.total_found} records`);
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format) => {
    if (!searchResults || searchResults.records.length === 0) return;

    setLoading(true);
    try {
      // Get unique vehicle numbers from results
      const vehicleNos = [...new Set(searchResults.records.map(r => r.vehicle_no))];
      
      if (vehicleNos.length === 1) {
        // Single vehicle - download directly
        const response = await fetch(`${BACKEND_URL}/api/mr/${vehicleNos[0]}/download/${format}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error('Download failed');
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `MR_${vehicleNos[0]}.${format}`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        // Multiple vehicles - export search results as CSV
        const csvContent = [
          ['Vehicle No', 'Date', 'Description', 'Vendor', 'Amount'].join(','),
          ...searchResults.records.map(r => 
            [r.vehicle_no, r.date, `"${(r.description || '').replace(/"/g, '""')}"`, `"${(r.vendor || '').replace(/"/g, '""')}"`, r.amount].join(',')
          )
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `MR_Search_Results.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      
      setMessage('✅ Downloaded successfully');
    } catch (error) {
      setMessage(`❌ Download error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={isMobile ? { padding: '1rem' } : {}}>
      <h3 className="card-title" style={isMobile ? { fontSize: '1rem' } : {}}>
        Search M&R Records
      </h3>
      <p style={{ marginBottom: '1rem', color: '#64748B', fontSize: isMobile ? '0.8rem' : undefined }}>
        Search across all vehicles by description, vendor, or vehicle number
      </p>

      {message && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{message}</div>}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Search description (e.g., brake, clutch)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ flex: 2, minWidth: '200px' }}
          data-testid="mr-search-query"
        />
        <input
          type="text"
          className="form-control"
          placeholder="Vehicle No"
          value={searchVehicle}
          onChange={(e) => setSearchVehicle(e.target.value)}
          style={{ flex: 1, minWidth: '100px' }}
          data-testid="mr-search-vehicle"
        />
        <input
          type="text"
          className="form-control"
          placeholder="Vendor"
          value={searchVendor}
          onChange={(e) => setSearchVendor(e.target.value)}
          style={{ flex: 1, minWidth: '100px' }}
          data-testid="mr-search-vendor"
        />
        <button 
          className="btn btn-primary" 
          onClick={handleSearch} 
          disabled={loading}
          data-testid="mr-search-btn"
        >
          {loading ? 'Searching...' : '🔍 Search'}
        </button>
        <button 
          className="btn btn-secondary"
          onClick={() => setShowFilters(!showFilters)}
          style={{ background: showFilters ? '#EEF2FF' : undefined }}
        >
          ⚙️ Filters
        </button>
      </div>

      {showFilters && (
        <div style={{ 
          background: '#F8FAFC', 
          padding: '1rem', 
          borderRadius: '8px', 
          marginBottom: '1rem',
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <div>
            <label style={{ fontSize: '0.75rem', color: '#64748B' }}>Date From</label>
            <input
              type="date"
              className="form-control"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ width: '140px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.75rem', color: '#64748B' }}>Date To</label>
            <input
              type="date"
              className="form-control"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ width: '140px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.75rem', color: '#64748B' }}>Min Cost ($)</label>
            <input
              type="number"
              className="form-control"
              value={costMin}
              onChange={(e) => setCostMin(e.target.value)}
              style={{ width: '100px' }}
              placeholder="0"
            />
          </div>
          <div>
            <label style={{ fontSize: '0.75rem', color: '#64748B' }}>Max Cost ($)</label>
            <input
              type="number"
              className="form-control"
              value={costMax}
              onChange={(e) => setCostMax(e.target.value)}
              style={{ width: '100px' }}
              placeholder="∞"
            />
          </div>
        </div>
      )}

      {searchResults && searchResults.records.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <button 
              className="btn btn-success" 
              onClick={() => handleDownload('csv')}
              disabled={loading}
              style={{ fontSize: '0.85rem' }}
            >
              📥 Download CSV
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => handleDownload('xlsx')}
              disabled={loading}
              style={{ fontSize: '0.85rem' }}
            >
              📥 Download Excel
            </button>
          </div>

          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Vendor</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.records.map((record, idx) => (
                  <tr key={idx}>
                    <td><span className="vin-plate" style={{ fontSize: '0.75rem' }}>{record.vehicle_no}</span></td>
                    <td style={{ fontSize: '0.8rem' }}>{record.date}</td>
                    <td style={{ fontSize: '0.8rem', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={record.description}>
                      {record.description}
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>{record.vendor}</td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>
                      ${record.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ textAlign: 'center', color: '#64748B', fontSize: '0.8rem', marginTop: '0.5rem' }}>
            Showing {searchResults.records.length} of {searchResults.total_found} records
          </p>
        </>
      )}

      {searchResults && searchResults.records.length === 0 && (
        <p style={{ textAlign: 'center', color: '#64748B', padding: '2rem' }}>
          No records found matching your search criteria
        </p>
      )}
    </div>
  );
}

export default MRReport;
